import { useEffect, useState, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';

/**
 * Full-screen error feedback shown when a clock-in or clock-out fails.
 *
 * Displays an animated X mark, the error message, a retry button, and
 * auto-dismisses back to the identify screen after a countdown.
 */
export default function KioskErrorScreen({
  errorMessage,
  goToIdentify,
  goToIdle,
  autoReturnSeconds = 5,
}) {
  const [countdown, setCountdown] = useState(autoReturnSeconds);
  const goToIdleRef = useRef(goToIdle);
  goToIdleRef.current = goToIdle;

  // -------------------------------------------------------------------
  // Countdown timer
  // -------------------------------------------------------------------
  useEffect(() => {
    if (countdown <= 0) {
      goToIdleRef.current?.();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // -------------------------------------------------------------------
  // Shake animation for the X icon
  // -------------------------------------------------------------------
  const shakeVariants = {
    initial: { rotate: 0 },
    shake: {
      rotate: [0, -8, 8, -6, 6, -3, 3, 0],
      transition: { duration: 0.6, delay: 0.8 },
    },
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-red-500 via-red-600 to-red-700 select-none"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Animated X mark with shake */}
      <motion.div
        variants={shakeVariants}
        initial="initial"
        animate="shake"
      >
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <svg viewBox="0 0 100 100" className="h-32 w-32">
            <motion.circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="white"
              strokeWidth="3"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            />
            <motion.path
              d="M35 35 L65 65"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.3, delay: 0.4, ease: 'easeOut' }}
            />
            <motion.path
              d="M65 35 L35 65"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.3, delay: 0.55, ease: 'easeOut' }}
            />
          </svg>
        </motion.div>
      </motion.div>

      {/* Title */}
      <motion.h1
        className="mt-8 text-4xl font-bold text-white"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        Something went wrong
      </motion.h1>

      {/* Error message */}
      <motion.p
        className="mt-4 max-w-md text-center text-xl text-white/80"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.45 }}
      >
        {errorMessage}
      </motion.p>

      {/* Retry button */}
      <motion.button
        className="mt-8 min-h-16 min-w-48 rounded-2xl border-2 border-white/80 px-10 py-4 text-xl font-semibold text-white transition-colors hover:bg-white/10 active:bg-white/20"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        whileTap={{ scale: 0.95 }}
        onClick={goToIdentify}
      >
        Try Again
      </motion.button>

      {/* Countdown */}
      <motion.p
        className="mt-8 text-lg text-white/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.8 }}
      >
        Returning in {countdown}s...
      </motion.p>
    </motion.div>
  );
}
